/**
 * Greystone Quick Buy Modal
 * Intercepts [data-quick-buy] product links, shows a popup with product details,
 * and redirects to Shopify checkout via /cart/{variant_id}:{quantity}
 */
(function() {
  'use strict';

  var productCache = {};
  var modal = null;
  var isOpen = false;
  var currentVariantId = null;
  var currentProduct = null;

  // Product specs (not available from /products/{handle}.js)
  var PRODUCT_SPECS = {
    'greystone-yard-vac-200': {
      tagline: 'Perfect for Small Properties',
      specs: [
        'Push-around design \u2014 no tow vehicle needed',
        '200L capacity \u2014 1\u20132 wheelbarrow loads',
        'Honda 50cc 4-stroke engine',
        'Perfect for yards, stalls & small paddocks'
      ],
      monthly: '$110/mo'
    },
    'greystone-pasture-vac-400': {
      tagline: 'Great for Mid-Size Farms',
      specs: [
        'Towable behind ATV, mower or golf cart',
        '400L capacity \u2014 3\u20134 wheelbarrow loads',
        'Honda 4-stroke engine \u2014 Removable back door',
        'Handles wet, dry & frozen manure effortlessly'
      ],
      monthly: '$137/mo'
    },
    'greystone-pasture-vac-800': {
      tagline: 'Best for Large Properties',
      specs: [
        'Towable \u2014 Heavy-duty steel frame construction',
        '800L capacity \u2014 5\u20136 wheelbarrow loads',
        'Honda 4-stroke engine \u2014 Tipping action',
        'Best for large farms & commercial properties'
      ],
      monthly: '$172/mo'
    }
  };

  function createModal() {
    var overlay = document.createElement('div');
    overlay.id = 'quick-buy-modal';
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('role', 'dialog');
    overlay.style.cssText = 'display:none; position:fixed; inset:0; z-index:9999;';

    overlay.innerHTML =
      '<div style="position:fixed; inset:0; background:rgba(0,0,0,0.6); backdrop-filter:blur(4px);" data-qb-backdrop></div>' +
      '<div style="position:fixed; inset:0; display:flex; align-items:center; justify-content:center; padding:1rem;">' +
        '<div style="background:hsl(var(--card)); border-radius:1rem; box-shadow:0 25px 50px -12px rgba(0,0,0,0.25); width:100%; max-width:32rem; max-height:90vh; overflow-y:auto; position:relative;" data-qb-panel>' +
          // Close button
          '<button style="position:absolute; top:0.75rem; right:0.75rem; z-index:10; background:hsl(var(--secondary)); border-radius:9999px; padding:0.5rem; border:none; cursor:pointer; line-height:0;" data-qb-close aria-label="Close">' +
            '<svg width="20" height="20" fill="none" stroke="hsl(var(--foreground))" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>' +
          '</button>' +
          // Loading state
          '<div style="padding:3rem; text-align:center;" data-qb-loading>' +
            '<div style="display:inline-block; width:2rem; height:2rem; border:2px solid hsl(var(--accent)); border-top-color:transparent; border-radius:9999px; animation:qb-spin 0.8s linear infinite;"></div>' +
            '<p style="color:hsl(var(--muted-foreground)); font-size:0.875rem; margin-top:0.75rem;">Loading product...</p>' +
          '</div>' +
          // Error state
          '<div style="padding:3rem; text-align:center; display:none;" data-qb-error>' +
            '<p style="color:hsl(var(--destructive)); font-weight:600; margin-bottom:0.5rem;">Unable to load product</p>' +
            '<p style="color:hsl(var(--muted-foreground)); font-size:0.875rem; margin-bottom:1rem;">Please try again or visit the product page.</p>' +
            '<a href="#" style="color:hsl(var(--accent)); font-weight:600; font-size:0.875rem;" data-qb-fallback>Go to product page \u2192</a>' +
          '</div>' +
          // Product content
          '<div style="display:none;" data-qb-content>' +
            '<div style="aspect-ratio:4/3; background:hsl(var(--secondary)); overflow:hidden; border-radius:1rem 1rem 0 0;">' +
              '<img src="" alt="" style="width:100%; height:100%; object-fit:cover;" data-qb-image>' +
            '</div>' +
            '<div style="padding:1.5rem;">' +
              '<p style="color:hsl(var(--cta)); font-family:var(--font-heading,Outfit,sans-serif); font-weight:600; font-size:0.75rem; text-transform:uppercase; letter-spacing:0.1em; margin-bottom:0.25rem;">Greystone USA</p>' +
              '<h3 style="font-size:1.25rem; font-family:var(--font-heading,Outfit,sans-serif); font-weight:900; color:hsl(var(--foreground)); margin:0;" data-qb-title></h3>' +
              '<p style="color:hsl(var(--muted-foreground)); font-size:0.875rem; margin-top:0.25rem;" data-qb-tagline></p>' +
              // Price block
              '<div style="border-top:1px solid hsl(var(--border)); border-bottom:1px solid hsl(var(--border)); padding:0.75rem 0; margin:1rem 0;">' +
                '<div style="display:flex; align-items:baseline; gap:0.5rem;">' +
                  '<span style="font-size:1.875rem; font-family:var(--font-heading,Outfit,sans-serif); font-weight:800; color:hsl(var(--accent));" data-qb-price></span>' +
                  '<span style="color:hsl(var(--muted-foreground)); font-size:0.875rem;">USD</span>' +
                '</div>' +
                '<p style="font-size:0.75rem; color:hsl(var(--muted-foreground)); margin-top:0.125rem;">+ Flat Rate Shipping</p>' +
                '<p style="font-size:0.75rem; color:hsl(var(--cta)); font-weight:600; margin-top:0.125rem;" data-qb-monthly></p>' +
              '</div>' +
              // Specs
              '<ul style="list-style:none; padding:0; margin:0 0 1rem 0;" data-qb-specs></ul>' +
              // Product options (forklift, towing, etc.)
              '<div data-qb-options style="margin-bottom:1rem;"></div>' +
              // Quantity
              '<div style="display:flex; align-items:center; gap:0.75rem; margin-bottom:1rem;">' +
                '<label style="font-size:0.875rem; font-weight:600; color:hsl(var(--foreground));">Qty:</label>' +
                '<div style="display:flex; align-items:center; border:1px solid hsl(var(--border)); border-radius:0.5rem; overflow:hidden;">' +
                  '<button style="padding:0.5rem 0.75rem; background:none; border:none; cursor:pointer; font-size:1.125rem; font-weight:700; color:hsl(var(--foreground)); line-height:1;" data-qb-minus>\u2212</button>' +
                  '<span style="width:2.5rem; text-align:center; padding:0.5rem 0; font-size:0.875rem; font-weight:600; color:hsl(var(--foreground)); border-left:1px solid hsl(var(--border)); border-right:1px solid hsl(var(--border));" data-qb-qty>1</span>' +
                  '<button style="padding:0.5rem 0.75rem; background:none; border:none; cursor:pointer; font-size:1.125rem; font-weight:700; color:hsl(var(--foreground)); line-height:1;" data-qb-plus>+</button>' +
                '</div>' +
              '</div>' +
              // Buy button
              '<a href="#" style="display:flex; align-items:center; justify-content:center; gap:0.5rem; width:100%; background:hsl(var(--cta)); color:hsl(var(--cta-foreground)); font-family:var(--font-heading,Outfit,sans-serif); font-weight:700; padding:1rem; border-radius:0.75rem; text-decoration:none; font-size:1rem; transition:background 0.15s;" data-qb-checkout>' +
                'Buy Now' +
                '<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>' +
              '</a>' +
              // Trust
              '<div style="display:flex; flex-wrap:wrap; gap:1rem; font-size:0.75rem; color:hsl(var(--muted-foreground)); padding-top:0.75rem;">' +
                '<span>\uD83D\uDD12 Secure checkout</span>' +
                '<span>\uD83D\uDE9A Flat rate shipping</span>' +
                '<span>\uD83C\uDDFA\uD83C\uDDF8 Honda-powered</span>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);
    modal = overlay;

    // Spin animation
    if (!document.getElementById('qb-styles')) {
      var style = document.createElement('style');
      style.id = 'qb-styles';
      style.textContent =
        '@keyframes qb-spin { to { transform: rotate(360deg); } }' +
        '#quick-buy-modal [data-qb-checkout]:hover { background: hsl(var(--cta-hover)) !important; }' +
        '#quick-buy-modal [data-qb-checkout].qb-disabled { opacity:0.5; pointer-events:none; }' +
        '#quick-buy-modal [data-qb-close]:hover { background: hsl(var(--border)) !important; }' +
        '#quick-buy-modal [data-qb-minus]:hover, #quick-buy-modal [data-qb-plus]:hover { background: hsl(var(--secondary)); }' +
        '#quick-buy-modal .qb-select { width:100%; padding:0.625rem 0.75rem; border:1px solid hsl(var(--border)); border-radius:0.5rem; background:hsl(var(--card)); color:hsl(var(--foreground)); font-size:0.875rem; font-family:inherit; appearance:none; -webkit-appearance:none; cursor:pointer; background-image:url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'none\' stroke=\'%23888\' viewBox=\'0 0 24 24\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M6 9l6 6 6-6\'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 0.75rem center; }' +
        '#quick-buy-modal .qb-select:focus { outline:none; border-color:hsl(var(--cta)); box-shadow:0 0 0 2px hsl(var(--cta)/0.2); }' +
        '#quick-buy-modal .qb-select.qb-invalid { border-color:hsl(var(--destructive)); }' +
        '#quick-buy-modal .qb-option-error { color:hsl(var(--destructive)); font-size:0.75rem; margin-top:0.25rem; display:none; }';
      document.head.appendChild(style);
    }

    // Event listeners
    modal.querySelector('[data-qb-backdrop]').addEventListener('click', closeModal);
    modal.querySelector('[data-qb-close]').addEventListener('click', closeModal);

    modal.querySelector('[data-qb-checkout]').addEventListener('click', function(e) {
      var selects = modal.querySelectorAll('[data-qb-option]');
      var hasError = false;
      for (var i = 0; i < selects.length; i++) {
        if (!selects[i].value) {
          selects[i].classList.add('qb-invalid');
          var err = selects[i].parentNode.querySelector('.qb-option-error');
          if (err) err.style.display = 'block';
          hasError = true;
        }
      }
      if (hasError || !currentVariantId) {
        e.preventDefault();
      }
    });

    modal.querySelector('[data-qb-minus]').addEventListener('click', function() {
      var el = modal.querySelector('[data-qb-qty]');
      var val = parseInt(el.textContent) || 1;
      if (val > 1) { el.textContent = val - 1; updateCheckoutUrl(); }
    });

    modal.querySelector('[data-qb-plus]').addEventListener('click', function() {
      var el = modal.querySelector('[data-qb-qty]');
      var val = parseInt(el.textContent) || 1;
      if (val < 10) { el.textContent = val + 1; updateCheckoutUrl(); }
    });
  }

  function updateCheckoutUrl() {
    if (!modal || !currentVariantId) return;
    var qty = parseInt(modal.querySelector('[data-qb-qty]').textContent) || 1;
    modal.querySelector('[data-qb-checkout]').href = '/cart/' + currentVariantId + ':' + qty;
  }

  function openModal(handle, fallbackUrl) {
    if (!modal) createModal();

    modal.style.display = 'block';
    modal.querySelector('[data-qb-loading]').style.display = 'block';
    modal.querySelector('[data-qb-error]').style.display = 'none';
    modal.querySelector('[data-qb-content]').style.display = 'none';
    isOpen = true;
    document.body.style.overflow = 'hidden';

    // Reset quantity
    modal.querySelector('[data-qb-qty]').textContent = '1';

    if (productCache[handle]) {
      populateModal(productCache[handle], handle);
      return;
    }

    fetch('/products/' + handle + '.js')
      .then(function(res) {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(function(product) {
        productCache[handle] = product;
        if (isOpen) populateModal(product, handle);
      })
      .catch(function() {
        if (!isOpen) return;
        modal.querySelector('[data-qb-loading]').style.display = 'none';
        modal.querySelector('[data-qb-error]').style.display = 'block';
        modal.querySelector('[data-qb-fallback]').href = fallbackUrl;
      });
  }

  function populateModal(product, handle) {
    var info = PRODUCT_SPECS[handle] || {};
    currentProduct = product;

    modal.querySelector('[data-qb-loading]').style.display = 'none';
    modal.querySelector('[data-qb-content]').style.display = 'block';

    modal.querySelector('[data-qb-image]').src = product.featured_image;
    modal.querySelector('[data-qb-image]').alt = product.title;
    modal.querySelector('[data-qb-title]').textContent = product.title;
    modal.querySelector('[data-qb-tagline]').textContent = info.tagline || '';

    // Shopify returns price in cents
    var price = '$' + (product.price / 100).toLocaleString('en-US');
    modal.querySelector('[data-qb-price]').textContent = price;
    modal.querySelector('[data-qb-monthly]').textContent =
      info.monthly ? '\uD83D\uDCB3 from ' + info.monthly + ' with Shop Pay \u00B7 0% APR available' : '';

    // Specs
    var specsEl = modal.querySelector('[data-qb-specs]');
    specsEl.innerHTML = '';
    if (info.specs) {
      info.specs.forEach(function(spec) {
        var li = document.createElement('li');
        li.style.cssText = 'display:flex; align-items:flex-start; gap:0.625rem; font-size:0.875rem; color:hsl(var(--foreground)); margin-bottom:0.5rem;';
        li.innerHTML = '<span style="color:hsl(var(--cta)); font-weight:700; flex-shrink:0; font-size:1rem; line-height:1; margin-top:0.125rem;">\u2713</span> ' + spec;
        specsEl.appendChild(li);
      });
    }

    // Product options (forklift, towing, etc.)
    buildOptions(product);
    matchVariant();
  }

  function buildOptions(product) {
    var container = modal.querySelector('[data-qb-options]');
    container.innerHTML = '';

    if (!product.options || product.options.length === 0) return;
    // Skip if the only option is "Default Title" (no real options)
    if (product.options.length === 1 && product.options[0].name === 'Title') return;

    product.options.forEach(function(opt, i) {
      var wrapper = document.createElement('div');
      wrapper.style.cssText = 'margin-bottom:0.75rem;';

      var label = document.createElement('label');
      label.style.cssText = 'display:block; font-size:0.875rem; font-weight:600; color:hsl(var(--foreground)); margin-bottom:0.375rem;';
      label.textContent = opt.name;

      var select = document.createElement('select');
      select.className = 'qb-select';
      select.setAttribute('data-qb-option', i);

      // Get unique values for this option, filtering out "Please Select"
      var values = opt.values || [];
      // Add placeholder
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Please Select';
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      values.forEach(function(val) {
        if (val === 'Please Select') return;
        var option = document.createElement('option');
        option.value = val;
        option.textContent = val;
        select.appendChild(option);
      });

      var errorMsg = document.createElement('p');
      errorMsg.className = 'qb-option-error';
      errorMsg.textContent = 'Please select an option';

      select.addEventListener('change', function() {
        select.classList.remove('qb-invalid');
        errorMsg.style.display = 'none';
        matchVariant();
      });

      wrapper.appendChild(label);
      wrapper.appendChild(select);
      wrapper.appendChild(errorMsg);
      container.appendChild(wrapper);
    });
  }

  function matchVariant() {
    if (!currentProduct || !modal) return;

    var selects = modal.querySelectorAll('[data-qb-option]');
    var selections = [];
    var allSelected = true;

    for (var i = 0; i < selects.length; i++) {
      var val = selects[i].value;
      selections.push(val);
      if (!val) allSelected = false;
    }

    var btn = modal.querySelector('[data-qb-checkout]');

    if (!allSelected || selects.length === 0 && currentProduct.variants.length === 1) {
      // If no options or single variant, just use first variant
      if (selects.length === 0) {
        currentVariantId = currentProduct.variants[0].id;
        btn.classList.remove('qb-disabled');
        updateCheckoutUrl();
        return;
      }
      currentVariantId = null;
      btn.classList.add('qb-disabled');
      btn.removeAttribute('href');
      return;
    }

    // Find matching variant
    var matched = null;
    currentProduct.variants.forEach(function(v) {
      var isMatch = true;
      for (var j = 0; j < selections.length; j++) {
        var optionKey = 'option' + (j + 1);
        if (v[optionKey] !== selections[j]) { isMatch = false; break; }
      }
      if (isMatch) matched = v;
    });

    if (matched) {
      currentVariantId = matched.id;
      btn.classList.remove('qb-disabled');
      updateCheckoutUrl();
    } else {
      currentVariantId = null;
      btn.classList.add('qb-disabled');
      btn.removeAttribute('href');
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.style.display = 'none';
    isOpen = false;
    document.body.style.overflow = '';
    currentVariantId = null;
    currentProduct = null;
  }

  function extractHandle(href) {
    var match = (href || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function init() {
    document.addEventListener('click', function(e) {
      var link = e.target.closest('[data-quick-buy]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      var href = link.getAttribute('href') || '';
      var handle = extractHandle(href);
      if (!handle) {
        window.location.href = href;
        return;
      }

      openModal(handle, href);
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) closeModal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function() {
    productCache = {};
  });
})();
